import type { ReactElement } from 'react';
import React, { useCallback } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useScrollIntoView } from 'hooks/useScrollIntoView';
import { useResponsesLevelPairs } from 'pages/reports/hooks/useUserRelationIds';
import { useArea } from 'services/assist-api/entities/area/areas';
import { useReferenceDataCategoryNationals } from 'services/assist-api/entities/reference-data-category-national/reference-datas-category-nationals';
import type { BaseReportResponse } from 'services/assist-api/entities/test-instance-response/reports/report-responses';
import { useTestCategoriesByTestId } from 'services/assist-api/entities/test-item/test-items';
import { useTest } from 'services/assist-api/entities/test/tests';
import { isError, isInitialLoad } from 'utils/check-query-status';
import CategoryCard from './category-card';
import Skeleton from './skeleton';

export type MinimalResponse = {
  id: number;
  stanine: number;
  scaleScore: number;
  scaleError: number;
  status: BaseReportResponse['status'];
  responseCategories: BaseReportResponse['responseCategories'];
  testInstanceUser?: {
    id: number;
    identifier?: string;
    firstname?: string;
    lastname?: string;
    gender?: API_JSON;
    level: { id: number };
    referenceLevel: { id: number };
  };
};

export type Props = {
  isReferenceDataVisible: boolean;
  period: { startDate: string; endDate: string };
  testIds: number[];
  areaId: number;
  queryResponses: {
    queries: UseQueryResult<MinimalResponse[]>[];
    data: MinimalResponse[] | undefined;
  };
};

export default function CategoryReport({
  isReferenceDataVisible,
  period,
  testIds,
  areaId,
  queryResponses
}: Props): ReactElement {
  const testId = testIds[0];
  const test = useTest(testId, {}, { enabled: testIds?.length === 1 });
  const area = useArea(areaId);
  const testCategories = useTestCategoriesByTestId(testId);

  const responses = queryResponses.data;
  const levelPairs = useResponsesLevelPairs(responses);
  const categoryNational = useReferenceDataCategoryNationals(
    { filters: { test: testId, level: levelPairs[0]?.referenceLevel } },
    { enabled: isReferenceDataVisible && levelPairs?.length === 1 }
  );

  const getCategoryNationalNorms = useCallback(
    (categoryId: number) =>
      isReferenceDataVisible
        ? categoryNational.data?.json.find((catNational) => catNational.category.id === categoryId)
        : undefined,
    [categoryNational.data?.json, isReferenceDataVisible]
  );

  /* The CategoryCard expects a slightly different data shape, so produce that here */
  const getUsersWithResponse = useCallback(
    (categoryId: number) =>
      responses?.map((response) => ({
        user: response.testInstanceUser,
        response: response.responseCategories.find((r) => r.category.id === categoryId)
      })) || [],
    [responses]
  );

  const viewRef = useScrollIntoView<HTMLDivElement>(
    queryResponses.queries.every((q) => q.isFetched),
    area.isFetched,
    test.isFetched
  );

  if (
    isInitialLoad(...queryResponses.queries, area, test) ||
    (isReferenceDataVisible && isInitialLoad(categoryNational))
  ) {
    return <Skeleton numCards={testCategories.length} isLoading />;
  }

  if (
    isError(...queryResponses.queries, area, test) ||
    (isReferenceDataVisible && isError(categoryNational))
  ) {
    return <Skeleton numCards={testCategories.length} isError />;
  }

  return (
    <div ref={viewRef}>
      {testCategories.map((category) => (
        <CategoryCard
          key={category.id}
          category={category}
          categoryNationalNorms={getCategoryNationalNorms(category.id)}
          usersWithResponse={getUsersWithResponse(category.id)}
          totalStudents={responses?.length || 0}
          referenceLevelPair={levelPairs.length === 1 ? levelPairs[0] : undefined}
          testName={test.data?.json.name}
          theme={area.data?.json.identifier}
          startDate={period.startDate}
          endDate={period.endDate}
        />
      ))}
    </div>
  );
}
